import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import RadioButton from "../../ui/RadioButton/RadioButton";
import classes from "../Infrastructure/Infrastructure.module.css";
import QuestionCard from "../../ui/QuestionCard/QuestionCard";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { Bounce, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";

const initialState = {
  hasSupplyChainSystem: "",
  hasLogisticsOfficer: "",
  hasStoreRoom: "",
  isLogisticsOfficerTrained: "",
  hasReagentRefrigerators: "",
};

const LogisticsSupplyChain = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState(["", ""]);
  const { user } = useAuth();

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  const sectionName = "logistics-supply"; // formType

  useEffect(() => {
    if (labId) {
      fetchQuestionnaire();
    }
  }, [labId]);

  const fetchQuestionnaire = async () => {
    try {
      setIsFetching(true);
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/${sectionName}`
      );

      if (res.data) {
        if (res.data.answers) {
          setFormData(res.data.answers);
          setEditMode(false); // ✅ answers exist → read-only by default
        } else {
          setFormData(initialState);
          setEditMode(true); // ✅ no answers → open in edit mode
        }
        setOwnerLabId(res.data.labId);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No existing questionnaire found for this lab.");
        setFormData(initialState);
        setEditMode(true); // ✅ new lab → start in edit mode
      } else {
        console.error("Error fetching questionnaire:", error);
        toast.error("Failed to load saved questionnaire");
      }
    } finally {
      setIsFetching(false);
    }
  };

  // --- pass disabled to inputs ---
  const handleChange = (field, value) => {
    if (isReadOnly) return; // block edits
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (section, key, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    try {
      setLoading(true);
      const payload = {
        labId,
        formType: sectionName,
        answers: formData,
      };

      const res = await axios.post(
        `${env.kataApiUrl}/api/questionnaire/${sectionName}`,
        payload
      );

      toast.success("Submitted successfully!");

      fetchQuestionnaire();
      setCurrentSet(0);
       setEditMode(false); // exit edit mode

      console.log(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const questionSets = [
    {
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="Is there a system for supply Chain Management, eLMIS?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="hasSupplyChainSystem"
                checked={formData.hasSupplyChainSystem === "Yes"}
                onChange={() => handleChange("hasSupplyChainSystem", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasSupplyChainSystem"
                checked={formData.hasSupplyChainSystem === "No"}
                onChange={() => handleChange("hasSupplyChainSystem", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={2}
            question="Does the Laboratory have a store Room for reagent and consumable storage?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="hasStoreRoom"
                checked={formData.hasStoreRoom === "Yes"}
                onChange={() => handleChange("hasStoreRoom", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasStoreRoom"
                checked={formData.hasStoreRoom === "No"}
                onChange={() => handleChange("hasStoreRoom", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox number={3} question="Is there a Logistics Officer?">
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasLogisticsOfficer"
                checked={formData.hasLogisticsOfficer === "Yes"}
                onChange={() => handleChange("hasLogisticsOfficer", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasLogisticsOfficer"
                checked={formData.hasLogisticsOfficer === "No"}
                onChange={() => handleChange("hasLogisticsOfficer", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={4}
            question="Is the Logistics Officer trained?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="isLogisticsOfficerTrained"
                checked={formData.isLogisticsOfficerTrained === "Yes"}
                onChange={() =>
                  handleChange("isLogisticsOfficerTrained", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="isLogisticsOfficerTrained"
                checked={formData.isLogisticsOfficerTrained === "No"}
                onChange={() => handleChange("isLogisticsOfficerTrained", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={5}
            question="Does the Laboratory have Refrigerators specifically for the Laboratory reagent storage?"
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasReagentRefrigerators"
                checked={formData.hasReagentRefrigerators === "Yes"}
                onChange={() => handleChange("hasReagentRefrigerators", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasReagentRefrigerators"
                checked={formData.hasReagentRefrigerators === "No"}
                onChange={() => handleChange("hasReagentRefrigerators", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
        </div>
      ),
    },
  ];

  const { subtitle, content } = questionSets[currentSet];

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
     <div
      className={`${classes.wrapper} ${
        paramLabId ? classes.fromParams : ""
      } ${isReadOnly ? classes.readOnly : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Logistics and supply chain management</h2>
        {userRole === "LABORATORY" && !editMode && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditMode(true)}
            title="Edit"
          />
        )}
      </div>
      {(!editMode && userRole !== "MINISTRY") ? (
        <div className={classes.infoText}>
          ✅ Your Logistics and supply chain management has been submitted.
        </div>
      ) : (
        content
      )}
      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          disabled={loading}
          doneText={loading ? "Submitting..." : "Submit"}
        />
      )}
    </div>
  );
};

export default LogisticsSupplyChain;
